/**
 * Customer Web Shell — responsive layout (PHASE W-1).
 *
 * Two surfaces in one component, switched by Tailwind responsive
 * classes — no JS viewport hooks:
 *   • Desktop (`md:` and up): persistent left sidebar matching the
 *     owner/admin pattern (logo + nav stack + log out).
 *   • Mobile (< md): bottom tab bar with Home / My Cards / QR /
 *     Profile, plus a slim top bar showing the OneStamp lockup and
 *     a notifications bell icon (inert this phase).
 *
 * Both surfaces target the same routes so the `<Outlet />` stays
 * canonical.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Home, CreditCard, Gift, QrCode, User, LogOut, Bell,
  History as HistoryIcon, Megaphone, Settings as SettingsIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';

// Primary tabs — desktop sidebar AND mobile bottom tab bar.
// Mobile bottom nav is intentionally capped at 5 entries (iOS HIG +
// Material guidance); secondary destinations live in `SECONDARY_TABS`
// for the desktop sidebar only.
const TABS = [
  { to: '/customer/home', label: 'Home', icon: Home, end: true, testID: 'customer-nav-home' },
  { to: '/customer/cards', label: 'My Cards', icon: CreditCard, end: false, testID: 'customer-nav-mycards' },
  { to: '/customer/rewards', label: 'Rewards', icon: Gift, end: false, testID: 'customer-nav-rewards' },
  { to: '/customer/qr', label: 'Stamp', icon: QrCode, end: false, testID: 'customer-nav-qr' },
  { to: '/customer/profile', label: 'Profile', icon: User, end: false, testID: 'customer-nav-profile' },
];

// PR-Audit-02B G-06 (2026-06-19) — desktop-only secondary destinations.
// These routes already exist under `/customer/*` (see App.js routes)
// but were not surfaced in the sidebar. Adding here only — NO new
// pages, NO route changes. Mobile bottom tab bar is unaffected.
const SECONDARY_TABS = [
  { to: '/customer/notifications', label: 'Notifications', icon: Bell, end: false, testID: 'customer-nav-notifications' },
  { to: '/customer/history', label: 'History', icon: HistoryIcon, end: false, testID: 'customer-nav-history' },
  { to: '/customer/promotions', label: 'Promotions', icon: Megaphone, end: false, testID: 'customer-nav-promotions' },
  { to: '/customer/settings', label: 'Settings', icon: SettingsIcon, end: false, testID: 'customer-nav-settings' },
];

export default function CustomerLayout() {
  const { user, signOut, token } = useAuthStore();
  const navigate = useNavigate();

  // Operator §3 — header bell unread badge. Polls every 30s and on
  // window focus. The endpoint is cheap (count_documents on an indexed
  // pair) so the cost is negligible. We deliberately do NOT touch
  // /api/me/notifications (the inbox listing) — keeps the badge query
  // independent of the inbox page's own cache.
  const unreadQ = useQuery({
    queryKey: qk.customerNotificationsUnread(),
    queryFn: () => api.customerNotificationsUnreadCount(token),
    enabled: !!token,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // Don't spam the network during dev hot-reloads.
    staleTime: 5_000,
  });
  const unread = unreadQ.data?.unread ?? 0;

  const onLogout = () => {
    signOut();
    navigate('/customer/login', { replace: true });
  };

  const onBellClick = () => {
    navigate('/customer/notifications');
  };

  const initial = (user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex" data-testid="customer-layout">
      {/* ===== DESKTOP SIDEBAR (md and up) ===== */}
      <aside
        className="hidden md:flex w-64 shrink-0 border-r border-white/5 bg-[#0E0E13] flex-col"
        data-testid="customer-sidebar"
      >
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/5">
          <img
            src="/onestamp-logo.png"
            alt="OneStamp"
            className="w-10 h-10 rounded-2xl shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs text-gold/85 uppercase tracking-wider font-semibold">
              OneStamp
            </div>
            <div className="text-sm font-semibold truncate">
              {user?.name ?? 'Customer'}
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="customer-sidebar-nav">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testID}
              className={({ isActive }) =>
                'flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors ' +
                (isActive
                  ? 'bg-gold/10 text-gold border border-gold/25 shadow-glow-gold-sm'
                  : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent')
              }
            >
              <t.icon size={16} />
              {t.label}
            </NavLink>
          ))}
          {/* PR-Audit-02B G-06 — secondary destinations (existing
              routes, surfaced in the sidebar). Visually demoted via a
              small uppercase label above the group. */}
          <div className="pt-5 pb-1 px-3 text-[10px] uppercase tracking-widest text-white/30 font-semibold">
            More
          </div>
          {SECONDARY_TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testID}
              className={({ isActive }) =>
                'flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors ' +
                (isActive
                  ? 'bg-gold/10 text-gold border border-gold/25 shadow-glow-gold-sm'
                  : 'text-white/55 hover:bg-white/5 hover:text-white border border-transparent')
              }
            >
              <t.icon size={16} />
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-white/5">
          <button
            onClick={onLogout}
            data-testid="customer-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-2xl text-sm text-white/70 hover:bg-white/5 hover:text-white"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* ===== MAIN COLUMN (both viewports) ===== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — slimmer on mobile, parity-styled on desktop */}
        <header className="h-14 md:h-16 px-4 md:px-8 border-b border-white/5 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <img
              src="/onestamp-logo.png"
              alt="OneStamp"
              className="w-8 h-8 rounded-xl"
            />
            <span className="text-xs text-gold uppercase tracking-widest font-semibold">
              OneStamp
            </span>
          </div>
          <div className="hidden md:block text-sm text-white/60">
            {/* W-Customer Phase 2 — brand purity: drop the "Customer
                Portal" pill. Logo lockup + page content carry the
                identity on their own; the desktop top bar now reads
                empty on the left to mirror the consumer-app feel of
                the mobile surface. */}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBellClick}
              data-testid="customer-bell"
              className="relative w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 grid place-items-center text-white/60 hover:text-gold transition-colors"
              aria-label="Notifications"
              title="Notifications"
            >
              <Bell size={16} />
              {unread > 0 ? (
                <span
                  data-testid="customer-bell-badge"
                  className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-gold text-black text-[10px] font-bold leading-none grid place-items-center border border-[#0B0B0F]"
                  aria-label={`${unread} unread notifications`}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </button>
            <div className="hidden md:block text-right">
              <div className="text-sm font-medium">{user?.name ?? user?.email}</div>
              <div className="text-xs text-gold font-mono">
                {user?.membership_id ?? '—'}
              </div>
            </div>
            <div
              className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold"
              data-testid="customer-avatar"
            >
              {initial}
            </div>
          </div>
        </header>

        {/* Page outlet */}
        <main className="flex-1 px-4 md:px-8 py-6 md:py-8 max-w-[1200px] w-full mx-auto pb-24 md:pb-8">
          <Outlet />
        </main>
      </div>

      {/* ===== MOBILE BOTTOM TAB BAR (< md) =====
          Substep 2e — iOS safe-area: `paddingBottom: env(safe-area-inset-bottom)`
          inline so the home-indicator gesture area doesn't sit on top of
          the icons on notched iPhones (and Android nav-pill devices in
          PWA mode). The `pb-24` on `<main>` already accounts for the
          base 58px nav; this just extends the tappable area downward. */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0E0E13]/95 backdrop-blur border-t border-white/8 flex items-stretch"
        data-testid="customer-bottom-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`${t.testID}-mobile`}
            className={({ isActive }) =>
              'flex-1 flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] uppercase tracking-wider ' +
              (isActive ? 'text-gold' : 'text-white/55 hover:text-white')
            }
          >
            <t.icon size={18} />
            {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
